export type SoapGoalPerformanceCompareResult = 'match' | 'differ' | 'unreadable';

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function parseGoalRows(raw: unknown): unknown[] | null {
  if (raw === undefined || raw === null || raw === '') return null;
  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return Array.isArray(value) ? value : null;
}

function goalId(row: Record<string, unknown>): string {
  return text(row.goal_id || row.id);
}

function trialEvidence(row: Record<string, unknown>): string {
  return text(row.evidence ?? row.evidenceNote ?? row.trial_evidence);
}

/** Equivalent numbers compare equal. Non-numeric accuracy is unreadable. */
function accuracyKey(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  const stripped = text(value).replace(/%/g, '').trim();
  if (!stripped) return '';
  if (!/^-?\d+(\.\d+)?$/.test(stripped)) return null;
  const numeric = Number(stripped);
  return Number.isFinite(numeric) ? String(numeric) : null;
}

function performanceMap(rows: unknown[]): Map<string, string> | null {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
    const record = row as Record<string, unknown>;
    const id = goalId(record);
    if (!id) return null;
    const accuracy = accuracyKey(record.accuracy);
    if (accuracy === null) return null;
    const key = `${accuracy}|${text(record.cueing)}|${trialEvidence(record)}`;
    const existing = map.get(id);
    if (existing && existing !== key) return null;
    map.set(id, key);
  }
  return map;
}

/**
 * Compare submitted Mobile SOAP goal performance with a saved snapshot.
 * Uses goal ID, accuracy, cueing, and trial evidence. Ignores order and description.
 */
export function compareSoapGoalPerformance(
  submitted: unknown,
  saved: unknown
): SoapGoalPerformanceCompareResult {
  const submittedRows = parseGoalRows(submitted);
  const savedRows = parseGoalRows(saved);
  if (!submittedRows || !savedRows) return 'unreadable';
  const submittedMap = performanceMap(submittedRows);
  const savedMap = performanceMap(savedRows);
  if (!submittedMap || !savedMap) return 'unreadable';
  if (submittedMap.size !== savedMap.size) return 'differ';
  for (const [id, key] of submittedMap) {
    if (savedMap.get(id) !== key) return 'differ';
  }
  return 'match';
}
